import { CURRENCY_STORAGE_KEY } from "@/lib/currency-display"

export type AnalyticsSurface =
  | "home"
  | "product"
  | "buy"
  | "checkout"
  | "thankyou"
  | "account"
  | "blog"
  | "help"
  | "legal"
  | "downloadapp"
  | "free_esim"
  | "countries"
  | "other"

export interface AnalyticsContext {
  platform: "web"
  surface: AnalyticsSurface
  app_context: "web" | "flutter_webview"
  locale: string
  currency?: string
  route_path: string
  route_name: string
  session_id: string
  anonymous_id: string
}

const SESSION_ID_KEY = "yoho_analytics_session_id"
const ANONYMOUS_ID_KEY = "yoho_analytics_anonymous_id"

function createBrowserId(prefix: string) {
  const cryptoApi = window.crypto
  const randomId = cryptoApi.randomUUID()
  return `${prefix}_${randomId}`
}

function readOrCreateStorageId(key: string, prefix: string) {
  const existingId = window.localStorage.getItem(key)
  if (existingId) return existingId

  const nextId = createBrowserId(prefix)
  window.localStorage.setItem(key, nextId)
  return nextId
}

export function getAnalyticsLocale(pathname: string) {
  const [, locale] = pathname.split("/")
  if (!locale) {
    throw new Error("[analytics] route path does not contain locale")
  }
  return locale
}

export function getAnalyticsSurface(pathname: string): AnalyticsSurface {
  const normalizedPath = pathname.replace(/\/$/, "") || "/"
  const pathWithoutLocale = normalizedPath.replace(/^\/[A-Za-z]{2}(?:-[A-Za-z]{2})?/, "") || "/"

  if (pathWithoutLocale === "/") return "home"
  if (pathWithoutLocale.startsWith("/product/")) return "product"
  if (pathWithoutLocale === "/buy") return "buy"
  if (pathWithoutLocale === "/checkout") return "checkout"
  if (pathWithoutLocale.startsWith("/thankyou")) return "thankyou"
  if (pathWithoutLocale.startsWith("/account")) return "account"
  if (pathWithoutLocale.startsWith("/blog")) return "blog"
  if (pathWithoutLocale.startsWith("/help-center")) return "help"
  if (pathWithoutLocale === "/downloadapp") return "downloadapp"
  if (pathWithoutLocale.startsWith("/freeesim") || pathWithoutLocale.startsWith("/free_esim")) return "free_esim"
  if (pathWithoutLocale.startsWith("/countries")) return "countries"
  if (
    pathWithoutLocale === "/privacy-policy" ||
    pathWithoutLocale === "/refund-policy" ||
    pathWithoutLocale === "/terms-of-conditions"
  ) {
    return "legal"
  }
  return "other"
}

export function getAnalyticsRouteName(pathname: string) {
  const surface = getAnalyticsSurface(pathname)
  if (surface !== "other") return surface

  const normalizedPath = pathname.replace(/\/$/, "") || "/"
  const pathWithoutLocale = normalizedPath.replace(/^\/[A-Za-z]{2}(?:-[A-Za-z]{2})?/, "") || "/"
  return pathWithoutLocale.replace(/^\//, "").replace(/\//g, "_") || "home"
}

export function buildAnalyticsContext(pathname: string): AnalyticsContext {
  if (typeof window === "undefined") {
    throw new Error("[analytics] analytics context requires a browser window")
  }

  return {
    platform: "web",
    surface: getAnalyticsSurface(pathname),
    app_context: window.flutterMethod ? "flutter_webview" : "web",
    locale: getAnalyticsLocale(pathname),
    ...(window.localStorage.getItem(CURRENCY_STORAGE_KEY)
      ? { currency: window.localStorage.getItem(CURRENCY_STORAGE_KEY) as string }
      : {}),
    route_path: pathname,
    route_name: getAnalyticsRouteName(pathname),
    session_id: readOrCreateStorageId(SESSION_ID_KEY, "sess"),
    anonymous_id: readOrCreateStorageId(ANONYMOUS_ID_KEY, "anon"),
  }
}
